import { computed, Injectable } from '@angular/core';
import { UserService } from '../../../../shared/services/user.service';
import { ServiceOptions } from '../../constants/service-options.enum';
import { RebookingService } from './rebooking.service';

@Injectable({
  providedIn: 'root',
})
export class GAService {
  constructor(
    private readonly rebookingService: RebookingService,
    private readonly userService: UserService
  ) {}

  private viewModel = computed(() => {
    const bookingState = this.rebookingService.state;
    const bookingData = bookingState.bookingData;
    const userData = this.userService?.userInfo;
    return {
      digitalID: userData?.['custom:digital_id'],
      customerID: userData?.sub,
      modelNumber: bookingData?.modelNumber,
      serialNumber: bookingData?.serialNumber,
      productName:
        bookingData?.manufacturer && bookingData?.itemType
          ? `${bookingData.manufacturer} ${bookingData.itemType.toLowerCase()}`
          : '',
      manufacturer: bookingData?.manufacturer,
      planNumber: this.rebookingService.state.planNumber || bookingData?.planNumber,
      claimedBookingReference: this.rebookingService.state.claimId,
    };
  });

  get vm() {
    return this.viewModel();
  }

  GA_EVENTS = {
    PAGE_LOAD: 'spa-pageview',
    GENERIC: 'genericGAEvent',
  };

  PAGE_NAMES = {
    REBOOKING_CONFIRMATION: 'claims-engineer-rebooking-confirmation',
    REBOOKING: 'claims-rebooking',
  };

  sendGATag(GAObject: object) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(GAObject);
  }

  KEY_GA_INFO = {
    CATEGORY: 'claims-2.0',
    CLIENT_NAME: 'DG Claims',
  };

  // shared data for initial pageviews
  PAGE_VIEW = computed(() => ({
    event: this.GA_EVENTS.PAGE_LOAD,
    category: this.KEY_GA_INFO.CATEGORY,
    clientName: this.KEY_GA_INFO.CLIENT_NAME,
    DigitalID: this.vm.digitalID,
    customerID: this.vm.customerID,
    claimGroup: 'dgx',
    claimedManufacturer: this.vm.manufacturer,
    claimedModelNumber: this.vm.modelNumber,
    claimedSerialNumber: this.vm.serialNumber,
    claimsLogin: 'my account',
    planNumber: this.vm.planNumber,
    productName: this.vm.productName,
  }));

  GENERIC_EVENT = computed(() => ({
    event: this.GA_EVENTS.GENERIC,
    category: this.KEY_GA_INFO.CATEGORY,
    clientName: this.KEY_GA_INFO.CLIENT_NAME,
    planNumber: this.vm.planNumber,
    productModel: this.vm.modelNumber,
    productName: this.vm.productName,
    DigitalID: this.vm.digitalID,
    customerID: this.vm.customerID,
  }));

  GA_REBOOKING = {
    PAGE_LOAD: computed(() => ({
      pagename: this.PAGE_NAMES.REBOOKING,
      ...this.PAGE_VIEW(),
    })),
    EVENT_FIRST_DATE: computed(() => ({
      pagename: this.PAGE_NAMES.REBOOKING,
      ...this.PAGE_VIEW(),
      event: this.GA_EVENTS.GENERIC,
      eventCategory: 'claims rebooking',
      eventAction: 'first date shown',
    })),
    EVENT_DAYS_AVAILABLE: computed(() => ({
      pagename: this.PAGE_NAMES.REBOOKING,
      ...this.PAGE_VIEW(),
      event: this.GA_EVENTS.GENERIC,
      eventCategory: 'claims rebooking',
      eventAction: 'number-of-days-available',
    })),
    CALENDAR_BLOCK: computed(() => ({
      pagename: this.PAGE_NAMES.REBOOKING,
      ...this.PAGE_VIEW(),
      event: this.GA_EVENTS.GENERIC,
      eventCategory: 'claims rebooking',
      eventAction: 'calendar-block',
    })),
    CONTINUE: computed(() => ({
      ...this.GENERIC_EVENT(),
      pagename: this.PAGE_NAMES.REBOOKING,
      eventCategory: 'claims rebooking',
      eventAction: 'book this time',
    })),
  };

  GA_CONFIRMATION = {
    PAGE_LOAD: computed(() => ({
      pagename: this.PAGE_NAMES.REBOOKING_CONFIRMATION,
      ...this.PAGE_VIEW(),
      claimedBookingReference: this.vm.claimedBookingReference || '',
      optionType: ServiceOptions.HomeRepair,
      repairMethod: 'engineer',
    })),
    CONFIRMATION_EVENT: computed(() => ({
      pagename: this.PAGE_NAMES.REBOOKING_CONFIRMATION,
      ...this.PAGE_VIEW(),
      event: this.GA_EVENTS.GENERIC,
      eventCategory: 'claims rebooking confirmation',
      eventAction: 'claim confirmed',
      eventLabel: 'engineer',
      claimedBookingReference: this.vm.claimedBookingReference || '',
      optionType: ServiceOptions.HomeRepair,
      repairMethod: 'engineer',
    })),
    MMR: computed(() => ({
      pagename: this.PAGE_NAMES.REBOOKING_CONFIRMATION,
      ...this.PAGE_VIEW(),
      event: this.GA_EVENTS.GENERIC,
      eventCategory: 'claims rebooking confirmation',
      eventAction: 'claim confirmed CTA',
      eventLabel: 'Manage my Repair',
      claimedBookingReference: this.vm.claimedBookingReference || '',
      optionType: ServiceOptions.HomeRepair,
      repairMethod: 'engineer',
    })),
  };
}
